import React, { useContext, useState, useEffect, useRef } from "react";
import { useParams, useHistory } from "react-router-dom";

import { toast } from "react-toastify";
import openSocket from "../../services/socket-io";
import clsx from "clsx";

import { IconButton, Paper, Tooltip, makeStyles } from "@material-ui/core";
import { ExpandLess, ExpandMore } from "@material-ui/icons";

import ContactDrawer from "../ContactDrawer";
import MessageInput from "../MessageInput/";
import TicketHeader from "../TicketHeader";
import TicketInfo from "../TicketInfo";
import TicketActionButtons from "../TicketActionButtons";
import TicketFloatingControls from "../TicketFloatingControls";
import TicketLabels from "../TicketLabels";
import MessagesList from "../MessagesList";
import api from "../../services/api";
import { ReplyMessageProvider } from "../../context/ReplyingMessage/ReplyingMessageContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import {
  dispatchActiveTicketContext,
  dispatchTicketToolsUpdated,
  ticketUpdatedEvent
} from "../../utils/ticketEvents";

const drawerWidth = 360;
const AI_TOOLS_OPEN_STORAGE_PREFIX = "ticketAiToolsOpen";

const getAiToolsOpenStorageKey = userId =>
  `${AI_TOOLS_OPEN_STORAGE_PREFIX}:${userId || "default"}`;

const getStoredAiToolsOpen = userId =>
  localStorage.getItem(getAiToolsOpenStorageKey(userId)) === "true";

const buildSidepanelTicketContext = (ticket, contact) => {
  if (!ticket?.id) return null;

  const resolvedContact = contact?.id ? contact : ticket.contact || {};
  const phoneNumber =
    resolvedContact.number ||
    resolvedContact.phoneNumber ||
    resolvedContact.phone ||
    "";

  return {
    source: "crm-ticket",
    ticketId: ticket.id,
    ticket: {
      ...ticket,
      contact: resolvedContact,
    },
    contactId: ticket.contactId || resolvedContact.id || "",
    contact: resolvedContact,
    name: resolvedContact.name || ticket.contactName || "",
    contactName: resolvedContact.name || ticket.contactName || "",
    phoneNumber,
    number: phoneNumber,
    phone: phoneNumber,
    chatId: ticket.contact?.remoteJid || ticket.chatId || resolvedContact.remoteJid || "",
    status: ticket.status || "",
    queueId: ticket.queueId || "",
    userId: ticket.userId || "",
    connectionId: ticket.connectionId || ticket.whatsappId || "",
  };
};

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100%",
    position: "relative",
    overflow: "hidden",
  },

  ticketInfo: {
    maxWidth: "50%",
    flexBasis: "50%",
    [theme.breakpoints.down("sm")]: {
      maxWidth: "calc(100% - 96px)",
      flexBasis: "calc(100% - 96px)",
      minWidth: 0,
    },
  },
  ticketActionButtons: {
    maxWidth: "50%",
    flexBasis: "50%",
    display: "flex",
    [theme.breakpoints.down("sm")]: {
      maxWidth: "100%",
      flexBasis: "100%",
      marginBottom: "5px",
    },
  },
  mobileOptionsToggle: {
    display: "none",
    [theme.breakpoints.down("sm")]: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: 44,
      height: 44,
      padding: theme.spacing(1),
      marginLeft: 20,
    },
  },
  mobileCollapsibleOptions: {
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  mobileOptionsOpen: {
    [theme.breakpoints.down("sm")]: {
      display: "flex",
      width: "100%",
      maxWidth: "100%",
    },
  },
  ticketLabelsOptions: {
    flexDirection: "column",
  },

  mainWrapper: {
    flex: 1,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    border: "none",
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    borderLeft: "0",
    marginRight: -drawerWidth,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },

  mainWrapperShift: {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginRight: 0,
  },
}));

const Ticket = () => {
  const { ticketId } = useParams();
  const history = useHistory();
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerInitialTab, setDrawerInitialTab] = useState("contact");
  const [loading, setLoading] = useState(true);
  const [contact, setContact] = useState({});
  const [ticket, setTicket] = useState({});
  const [mobileOptionsOpen, setMobileOptionsOpen] = useState(false);
  const [aiToolsOpen, setAiToolsOpen] = useState(false);
  const latestTicketId = useRef(ticketId);
  const hasLoadedTicket = useRef(false);

  useEffect(() => {
    latestTicketId.current = ticketId;
    setLoading(!hasLoadedTicket.current);
    setAiToolsOpen(getStoredAiToolsOpen(user?.id));
    const fetchTicket = async () => {
      try {
        const { data } = await api.get("/tickets/" + ticketId);

        if (String(latestTicketId.current) !== String(ticketId)) return;

        setContact(data.contact);
        setTicket(data);
        hasLoadedTicket.current = true;
        setLoading(false);
      } catch (err) {
        if (String(latestTicketId.current) === String(ticketId)) {
          setLoading(false);
        }
        toastError(err);
      }
    };

    fetchTicket();
  }, [ticketId, history, user?.id]);

  useEffect(() => {
    dispatchActiveTicketContext(buildSidepanelTicketContext(ticket, contact));
  }, [ticket, contact]);

  useEffect(() => {
    return () => {
      dispatchActiveTicketContext(null);
    };
  }, []);

  useEffect(() => {
    const socket = openSocket();

    socket.on("connect", () => socket.emit("joinChatBox", ticketId));

    socket.on("ticket", (data) => {
      const isCurrentTicket =
        String(data.ticket?.id || data.ticketId || "") === String(ticketId);
      if (!isCurrentTicket) return;

      if (data.action === "update") {
        setTicket(data.ticket);
      }

      if (data.action === "updateUnread") {
        setTicket(prevState => ({
          ...prevState,
          unreadMessages: 0,
        }));
      }

      if (data.action === "toolsUpdate") {
        dispatchTicketToolsUpdated({
          ticketId: data.ticketId || data.ticket?.id,
          tool: data.tool,
          isActive: data.isActive,
        });
      }

      if (data.action === "delete") {
        toast.success("Chat eliminado correctamente.");
        history.push("/tickets");
      }
    });

    socket.on("contact", (data) => {
      if (data.action === "update") {
        setContact((prevState) => {
          if (prevState.id === data.contact?.id) {
            return { ...prevState, ...data.contact };
          }
          return prevState;
        });
      }
    });

    const handleTicketUpdated = event => {
      const updatedTicket = event.detail?.ticket;
      if (String(updatedTicket?.id || "") !== String(ticketId)) return;
      setTicket(updatedTicket);
      if (updatedTicket.contact) {
        setContact(updatedTicket.contact);
      }
    };

    window.addEventListener(ticketUpdatedEvent, handleTicketUpdated);

    return () => {
      window.removeEventListener(ticketUpdatedEvent, handleTicketUpdated);
      socket.disconnect();
    };
  }, [ticketId, history]);

  const handleDrawerOpen = () => {
    setDrawerInitialTab("contact");
    setDrawerOpen(true);
  };

  const handleOpenSequencesDrawer = () => {
    setDrawerInitialTab("sequences");
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  const handleToggleAiTools = () => {
    setAiToolsOpen(prevState => {
      const nextState = !prevState;
      localStorage.setItem(
        getAiToolsOpenStorageKey(user?.id),
        String(nextState)
      );
      return nextState;
    });
  };

  return (
    <div className={classes.root} id="drawer-container">
      <Paper
        variant="outlined"
        elevation={0}
        className={clsx(classes.mainWrapper, {
          [classes.mainWrapperShift]: drawerOpen,
        })}
      >
        <TicketHeader loading={loading && !ticket.id}>
          <div className={classes.ticketInfo}>
            <TicketInfo
              contact={contact}
              ticket={ticket}
              onClick={handleDrawerOpen}
              action={
                <Tooltip title={mobileOptionsOpen ? "Ocultar opciones" : "Mostrar opciones"}>
                  <IconButton
                    className={classes.mobileOptionsToggle}
                    onClick={event => {
                      event.stopPropagation();
                      setMobileOptionsOpen(prevState => !prevState);
                    }}
                  >
                    {mobileOptionsOpen ? <ExpandLess fontSize="large" /> : <ExpandMore fontSize="large" />}
                  </IconButton>
                </Tooltip>
              }
            />
          </div>
          <div
            className={clsx(classes.ticketActionButtons, classes.mobileCollapsibleOptions, {
              [classes.mobileOptionsOpen]: mobileOptionsOpen,
            })}
          >
            <TicketActionButtons ticket={ticket} />
          </div>
        </TicketHeader>
        <div
          className={clsx(classes.mobileCollapsibleOptions, classes.ticketLabelsOptions, {
            [classes.mobileOptionsOpen]: mobileOptionsOpen,
          })}
        >
          <TicketLabels ticket={ticket} onUpdate={setTicket} />
        </div>
        <ReplyMessageProvider>
          <MessagesList
            ticketId={ticketId}
            isGroup={Boolean(ticket.isGroup || ticket.contact?.isGroup)}
            showAiTools={aiToolsOpen}
          ></MessagesList>
          <MessageInput
            ticketStatus={ticket.status}
            connectionId={ticket.connectionId}
            showAiTools={aiToolsOpen}
            onOpenSequences={handleOpenSequencesDrawer}
          />
          <TicketFloatingControls
            ticket={ticket}
            toolsOpen={aiToolsOpen}
            onToggleTools={handleToggleAiTools}
          />
        </ReplyMessageProvider>
      </Paper>
      <ContactDrawer
        open={drawerOpen}
        handleDrawerClose={handleDrawerClose}
        contact={contact}
        ticket={ticket}
        initialTab={drawerInitialTab}
        loading={loading && !contact.id}
      />
    </div>
  );
};

export default Ticket;

